import React, { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import './Settings.css';

const Settings = ({ config, onSave }) => {
  const [formData, setFormData] = useState(config);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setMessage({ type: '', text: '' });
  };

  const validatePaths = async () => {
    try {
      // Check journal file
      const journalCheck = await axios.post(getApiUrl('/api/file/exists'), {
        filePath: formData.journalPath
      });

      if (!journalCheck.data.exists) {
        setMessage({ type: 'error', text: 'Journal file not found at the specified path' });
        return false;
      }

      // Test hledger command
      await axios.post(getApiUrl('/api/hledger/accounts'), {
        journalPath: formData.journalPath
      });

      return true;
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Invalid journal file or hledger error: ' + (err.response?.data?.error || err.message)
      });
      return false;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    const isValid = await validatePaths();
    if (!isValid) {
      setSaving(false);
      return;
    }

    onSave(formData);
    setMessage({ type: 'success', text: 'Settings saved successfully!' });
    setSaving(false);

    // Clear success message after 3 seconds
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings? This will require you to set up again.')) {
      localStorage.removeItem('hledger-config');
      window.location.reload();
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Configure your hledger dashboard</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">File Paths</h2>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Journal File Path *</label>
              <input
                type="text"
                className="form-input"
                value={formData.journalPath}
                onChange={(e) => handleChange('journalPath', e.target.value)}
                required
              />
              <p className="form-help">
                Path to your main hledger journal file
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Transactions File Path</label>
              <input
                type="text"
                className="form-input"
                value={formData.transactionsPath || ''}
                onChange={(e) => handleChange('transactionsPath', e.target.value)}
                placeholder="/path/to/transactions.json"
              />
              <p className="form-help">
                Path to your recurring transactions JSON file (optional)
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Budget File Path</label>
              <input
                type="text"
                className="form-input"
                value={formData.budgetPath || ''}
                onChange={(e) => handleChange('budgetPath', e.target.value)}
                placeholder="/path/to/directives.journal"
              />
              <p className="form-help">
                Path to a separate budget/directives file (optional, if not in main journal)
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Current Configuration</h2>
          </div>
          <div className="card-body">
            <div className="config-display">
              <div className="config-row">
                <span className="config-key">Journal:</span>
                <code className="config-value">{formData.journalPath}</code>
              </div>
              {formData.transactionsPath && (
                <div className="config-row">
                  <span className="config-key">Transactions:</span>
                  <code className="config-value">{formData.transactionsPath}</code>
                </div>
              )}
              {formData.budgetPath && (
                <div className="config-row">
                  <span className="config-key">Budget:</span>
                  <code className="config-value">{formData.budgetPath}</code>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <div className="spinner"></div> Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
          <button type="button" className="btn btn-danger" onClick={handleReset}>
            Reset All Settings
          </button>
        </div>
      </form>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">About</h2>
        </div>
        <div className="card-body">
          <p style={{ marginBottom: '1rem' }}>
            <strong>hledger Dashboard</strong> - A modern web interface for hledger
          </p>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            This dashboard provides a visual interface for viewing and managing your hledger financial data.
            It includes features for tracking account balances, budgets, recurring transactions, and more.
          </p>
          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
            <a
              href="https://hledger.org"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm"
            >
              hledger Documentation
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
